import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Share2, Copy, ExternalLink, TrendingUp, Users, DollarSign, MousePointerClick, Loader2, Sparkles,
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import usePageSeo from "@/hooks/usePageSeo";

/**
 * Public but token-gated affiliate dashboard.
 *
 * Route: /affiliate/dashboard/:token
 * Fetches /api/affiliate/me?token=X to render the affiliate's own stats
 * without requiring them to be a signed-in DPP user (they're partners, not
 * customers). Rotate token from admin if a URL leaks.
 */
export default function AffiliateDashboard() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  usePageSeo({
    title: "Affiliate Dashboard — DPP",
    description: "Your DPP affiliate performance: clicks, signups, MAU, monthly earnings, and top-performing posts.",
    path: "/affiliate/dashboard",
    // Noindex — this is a private link per affiliate.
    ogType: "website",
    noindex: true,
  });

  useEffect(() => {
    // Legacy noindex meta write left in place as belt-and-braces alongside
    // the `noindex: true` flag we now pass to `usePageSeo` (which handles
    // create + cleanup itself). Duplicate meta tags are ignored by crawlers.
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/affiliate/me", { params: { token } });
        if (!cancelled) setData(r.data);
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.detail || "Dashboard not found");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  if (loading) {
    return <div className="max-w-5xl mx-auto py-20 grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>;
  }
  if (error || !data) {
    return (
      <div className="max-w-xl mx-auto py-20 text-center" data-testid="affiliate-dashboard-error">
        <h1 className="serif text-3xl">Dashboard not found</h1>
        <p className="text-muted-foreground mt-3">This link is invalid or has been rotated. Reach out to the DPP team for a fresh one.</p>
        <Button asChild className="rounded-full mt-6"><Link to="/affiliate">Apply for a new link →</Link></Button>
      </div>
    );
  }

  const { affiliate, stats, top_content } = data;
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const shareLink = `${origin}/?aff=${affiliate.slug}`;

  const copy = async (text) => {
    try { await navigator.clipboard.writeText(text); toast.success("Copied"); }
    catch { toast.error("Copy failed — select manually"); }
  };

  return (
    <div className="max-w-5xl mx-auto px-6 py-10" data-testid="affiliate-dashboard">
      {/* Hero */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <div className="mono text-[10px] uppercase tracking-[0.25em] text-secondary flex items-center gap-2">
            <Share2 className="w-3.5 h-3.5" /> Affiliate dashboard
          </div>
          <h1 className="serif text-4xl mt-1" data-testid="dashboard-name">{affiliate.name}</h1>
          <div className="mono text-xs text-muted-foreground mt-1">
            {affiliate.email} · rate <span className="text-primary">${affiliate.commission_per_mau.toFixed(2)} / MAU / mo</span>
            {affiliate.pending_review && <Badge variant="outline" className="ml-2 mono text-[9px] uppercase text-secondary border-secondary/40">Pending review</Badge>}
          </div>
        </div>
        <div className="rounded-md border border-border p-3 max-w-md w-full">
          <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Your share link</div>
          <div className="flex gap-2 items-center">
            <code className="flex-1 mono text-xs bg-muted/40 px-2 py-1.5 rounded truncate" data-testid="dashboard-share-link">{shareLink}</code>
            <Button size="sm" variant="outline" onClick={() => copy(shareLink)} data-testid="dashboard-copy" className="rounded-full">
              <Copy className="w-3.5 h-3.5" />
            </Button>
          </div>
          <div className="mono text-[10px] text-muted-foreground mt-2">
            Append UTM: <code>&utm_content=video-title</code> to see which post drove the traffic.
          </div>
        </div>
      </div>

      {/* Stat grid */}
      <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard icon={MousePointerClick} label="Clicks (all time)" value={stats.clicks.toLocaleString()} />
        <StatCard icon={Sparkles} label="Signups" value={stats.signups.toLocaleString()} />
        <StatCard icon={Users} label="Users (attributed)" value={stats.attributed_users.toLocaleString()} />
        <StatCard icon={TrendingUp} label="MAU (last 30d)" value={stats.mau_30d.toLocaleString()} accent />
      </div>

      <div className="mt-3 grid sm:grid-cols-3 gap-3">
        <StatCard icon={DollarSign} label="Monthly projected" value={`$${stats.monthly_commission_projection.toFixed(2)}`} accent />
        <StatCard icon={DollarSign} label="Total earned"      value={`$${stats.commission_earned.toFixed(2)}`} />
        <StatCard icon={DollarSign} label="Balance due"       value={`$${stats.commission_due.toFixed(2)}`} accent />
      </div>

      {/* Top posts */}
      <Card className="mt-8" data-testid="dashboard-top-posts">
        <CardContent className="p-6">
          <h2 className="serif text-2xl">Top performing posts</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Traffic broken out by <code>utm_content</code>. Append <code>&utm_content=YOUR-POST-SLUG</code> to your share link so we can attribute clicks per video/tweet/newsletter.
          </p>
          {top_content.length === 0 ? (
            <div className="mt-5 text-sm text-muted-foreground py-8 text-center border border-dashed border-border rounded-md">
              No <code>utm_content</code> tagged clicks yet. Try sharing <code className="text-primary">{shareLink}&amp;utm_content=first-post</code> and check back after a few visits.
            </div>
          ) : (
            <div className="mt-4 space-y-2">
              {top_content.map((row, i) => (
                <div key={row.content} className="flex items-center justify-between border-b border-border/40 last:border-0 py-2" data-testid={`top-content-${i}`}>
                  <span className="mono text-sm text-foreground/85 truncate">#{i+1} · {row.content}</span>
                  <span className="mono text-sm text-primary">{row.clicks} click{row.clicks === 1 ? "" : "s"}</span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Footer */}
      <div className="mt-8 text-xs text-muted-foreground">
        <p><b>Payouts:</b> issued on the 1st of each month for balances ≥ $25. Sub-threshold rolls over. <Link to="/trust-center" className="underline underline-offset-2">Full T&Cs</Link>.</p>
        <p className="mt-1"><b>Bookmark this URL.</b> It's your only way in — we don't require a login. If it leaks, DM the team and we'll rotate.</p>
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, accent }) {
  return (
    <div className={`rounded-md border p-4 ${accent ? "border-primary/30 bg-primary/[0.03]" : "border-border"}`}>
      <div className="flex items-center gap-2 mono text-[10px] uppercase tracking-widest text-muted-foreground">
        <Icon className="w-3.5 h-3.5" /> {label}
      </div>
      <div className={`serif mt-1 ${accent ? "text-2xl text-primary" : "text-2xl"}`}>{value}</div>
    </div>
  );
}
